import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { hrService } from "../services/hrService";

// ── Context ─────────────────────────────────────────────────────────────────
const HRContext = createContext(null);
export const useHR = () => useContext(HRContext);

let _empId = 6;
let _leaveId = 4;
let _pslId = 3;

export function HRProvider({ children }) {
  // Developer: Nithin | Date: 2026-07-26 | Added shifts state for employee shift management
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [paySetups, setPaySetups] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Helper to identify owner role/designation/name
  const isOwner = (item) => {
    if (!item) return false;
    const r = String(item.role || item.employeeRole || "").toLowerCase();
    const d = String(item.designation || "").toLowerCase();
    const n = String(item.employeeName || item.name || "").toLowerCase();
    return r === "owner" || d === "owner" || n.includes("owner");
  };

  // Fetch from API on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        console.log("[HRContext] Fetching initial data...");
        const [empRes, attRes, leaveRes, payRes, pslRes, statsRes, shiftRes] = await Promise.all([
          hrService.getEmployees().catch((err) => { console.error("[HRContext] getEmployees failed:", err); return null; }),
          hrService.getAttendance().catch((err) => { console.error("[HRContext] getAttendance failed:", err); return null; }),
          hrService.getLeaves().catch((err) => { console.error("[HRContext] getLeaves failed:", err); return null; }),
          hrService.getPaySetups().catch((err) => { console.error("[HRContext] getPaySetups failed:", err); return null; }),
          hrService.getPayslips().catch((err) => { console.error("[HRContext] getPayslips failed:", err); return null; }),
          hrService.getDashboardStats().catch((err) => { console.error("[HRContext] getDashboardStats failed:", err); return null; }),
          hrService.getShifts().catch((err) => { console.error("[HRContext] getShifts failed:", err); return null; }),
        ]);

        console.log("[HRContext] empRes response:", empRes);

        let ownerIds = new Set();
        if (empRes?.data?.data?.employees) {
          const rawEmps = empRes.data.data.employees;
          rawEmps.forEach(e => {
            if (isOwner(e)) {
              if (e.employeeId) ownerIds.add(String(e.employeeId));
              if (e._id) ownerIds.add(String(e._id));
              if (e.id) ownerIds.add(String(e.id));
            }
          });

          const list = rawEmps
            .map(e => ({ ...e, id: e.employeeId || e._id }))
            .filter(e => !isOwner(e) && !ownerIds.has(String(e.id)));
          setEmployees(list);
        }

        const isNotOwnerRecord = (r) => !isOwner(r) && !ownerIds.has(String(r.employeeId || r.id || r._id));

        if (attRes?.data?.data?.attendance) {
          setAttendance(attRes.data.data.attendance.map(a => ({ ...a, id: a._id })).filter(isNotOwnerRecord));
        }
        if (leaveRes?.data?.data?.leaves) {
          setLeaves(leaveRes.data.data.leaves.map(l => ({ ...l, id: l._id })).filter(isNotOwnerRecord));
        }
        if (payRes?.data?.data?.paySetups) {
          setPaySetups(payRes.data.data.paySetups.map(s => ({ ...s, id: s._id })).filter(isNotOwnerRecord));
        }
        if (pslRes?.data?.data?.payslips) {
          setPayslips(pslRes.data.data.payslips.map(p => ({ ...p, id: p._id })).filter(isNotOwnerRecord));
        }
        if (statsRes?.data?.data) {
          setDashboardStats(statsRes.data.data);
        }
        if (shiftRes?.data?.data?.shifts) {
          setShifts(shiftRes.data.data.shifts.map(s => ({ ...s, id: s._id })));
        }
      } catch (error) {
        console.error("[HRContext] Failed to fetch HR data from backend.", error);
      } finally {
        setIsLoading(false);
        setLoadingStats(false);
      }
    };
    fetchInitialData();
  }, []);

  // Auto-poll attendance, leaves, and dashboard stats every 30 seconds
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      try {
        const [attRes, leaveRes, statsRes] = await Promise.all([
          hrService.getAttendance().catch(() => null),
          hrService.getLeaves().catch(() => null),
          hrService.getDashboardStats().catch(() => null),
        ]);

        const isNotOwnerRecord = (r) => !isOwner(r);

        if (attRes?.data?.data?.attendance) {
          setAttendance(attRes.data.data.attendance.map(a => ({ ...a, id: a._id })).filter(isNotOwnerRecord));
        }
        if (leaveRes?.data?.data?.leaves) {
          setLeaves(leaveRes.data.data.leaves.map(l => ({ ...l, id: l._id })).filter(isNotOwnerRecord));
        }
        if (statsRes?.data?.data) {
          setDashboardStats(statsRes.data.data);
        }
      } catch (e) {
        console.error("[HRContext] Poll refresh failed:", e);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, []);

  const refreshDashboardStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const res = await hrService.getDashboardStats();
      if (res?.data?.data) {
        setDashboardStats(res.data.data);
      }
    } catch (e) {
      console.error("[HRContext] refreshDashboardStats failed:", e);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // ── Employees ────────────────────────────────────────────────────────────
  const addEmployee = useCallback(async (data) => {
    try {
      const res = await hrService.createEmployee(data);
      const newEmp = res?.data?.data?.employee;
      if (newEmp) {
        const formatted = { ...newEmp, id: newEmp.employeeId || newEmp._id };
        setEmployees((p) => [formatted, ...p]);
        return formatted;
      }
    } catch (e) {
      console.warn("API addEmployee failed", e);
      throw e;
    }
    return null;
  }, []);

  const updateEmployee = useCallback(async (id, data) => {
    try {
      const res = await hrService.updateEmployee(id, data);
      const updatedEmp = res?.data?.data?.employee;
      if (updatedEmp) {
        const formatted = { ...updatedEmp, id: updatedEmp.employeeId || updatedEmp._id };
        setEmployees((p) => p.map((e) => (e._id === id || e.id === id || String(e.employeeId) === String(updatedEmp.employeeId) ? formatted : e)));
        return;
      }
    } catch (e) {
      console.warn("API updateEmployee failed", e);
      throw e;
    }
  }, []);

  const toggleEmployeeStatus = useCallback(async (id) => {
    try {
      const res = await hrService.toggleEmployeeStatus(id);
      const updatedEmp = res?.data?.data?.employee;
      if (updatedEmp) {
        const formatted = { ...updatedEmp, id: updatedEmp.employeeId || updatedEmp._id };
        setEmployees((p) => p.map((e) => (e._id === id || e.id === id ? formatted : e)));
        return;
      }
    } catch (e) {
      console.warn("API toggleEmployeeStatus failed", e);
      throw e;
    }
  }, []);

  // Developer: Nithin | Date: 2026-07-26 | Shift CRUD callbacks wired to hrService
  // ── Shifts ───────────────────────────────────────────────────────────────
  const addShift = useCallback(async (data) => {
    try {
      const res = await hrService.createShift(data);
      const newShift = res?.data?.data?.shift;
      if (newShift) {
        const formatted = { ...newShift, id: newShift._id };
        setShifts((p) => [formatted, ...p]);
        return formatted;
      }
    } catch (e) {
      console.warn('API addShift failed', e);
      throw e;
    }
    return null;
  }, []);

  const editShift = useCallback(async (id, data) => {
    try {
      const res = await hrService.updateShift(id, data);
      const updated = res?.data?.data?.shift;
      if (updated) {
        const formatted = { ...updated, id: updated._id };
        setShifts((p) => p.map((s) => (s._id === id || s.id === id ? formatted : s)));
        return formatted;
      }
    } catch (e) {
      console.warn('API editShift failed', e);
      throw e;
    }
    return null;
  }, []);

  const removeShift = useCallback(async (id) => {
    try {
      await hrService.deleteShift(id);
      setShifts((p) => p.filter((s) => s.id !== id && s._id !== id));
    } catch (e) {
      console.warn('API removeShift failed', e);
      throw e;
    }
  }, []);

  // ── Attendance ───────────────────────────────────────────────────────────
  const markAttendance = useCallback(async (record) => {
    try {
      const res = await hrService.markAttendance(record);
      const newAtt = res?.data?.data?.attendance;
      if (newAtt) {
        const formatted = { ...newAtt, id: newAtt._id };
        setAttendance((prev) => {
          const exists = prev.findIndex(a => a.employeeId === formatted.employeeId && a.date === formatted.date);
          if (exists >= 0) {
            const next = [...prev];
            next[exists] = formatted;
            return next;
          }
          return [formatted, ...prev];
        });
        refreshDashboardStats();
      }
    } catch (e) {
      console.warn("API markAttendance failed", e);
      throw e;
    }
  }, [refreshDashboardStats]);

  const bulkMarkAttendance = useCallback(async (records) => {
    try {
      const res = await hrService.bulkMarkAttendance({ records });
      // Fetch fresh data from backend to ensure all bulk records are accurate
      const getRes = await hrService.getAttendance();
        if (getRes?.data?.data?.attendance) {
          setAttendance(getRes.data.data.attendance.map(a => ({ ...a, id: a._id })));
      }
      refreshDashboardStats();
      return res?.data?.data;
    } catch (e) {
      console.warn("API bulkMarkAttendance failed", e);
      throw e;
    }
  }, [refreshDashboardStats]);

  // ── Leaves ───────────────────────────────────────────────────────────────
  const applyLeave = useCallback(async (data) => {
    try {
      const res = await hrService.createLeave(data);
      const newLeave = res?.data?.data?.leave;
      if (newLeave) {
        const formatted = { ...newLeave, id: newLeave._id };
        setLeaves((p) => [formatted, ...p]);
        return formatted;
      }
    } catch (e) {
      console.warn("API applyLeave failed", e);
      throw e; // rethrow so useLeaves.save() can surface the error message
    }
    return null;
  }, []);

  const updateLeaveStatus = useCallback(async (id, status, remarks = "") => {
    try {
      const res = await hrService.updateLeaveStatus(id, status, remarks);
      const updatedLeave = res?.data?.data?.leave;
      if (updatedLeave) {
        const formatted = { ...updatedLeave, id: updatedLeave._id };
        setLeaves((p) => p.map((l) => (l._id === id || l.id === id ? formatted : l)));
        refreshDashboardStats();
        return;
      }
    } catch (e) {
      console.warn("API updateLeaveStatus failed", e);
    }
  }, [refreshDashboardStats]);

  const deleteLeave = useCallback(async (id) => {
    try {
      await hrService.deleteLeave(id);
      setLeaves((p) => p.filter((l) => l.id !== id && l._id !== id));
    } catch (e) {
      console.warn("API deleteLeave failed", e);
    }
  }, []);

  // ── Pay Setup ────────────────────────────────────────────────────────────
  const savePaySetup = useCallback(async (employeeId, data) => {
    try {
      const res = await hrService.savePaySetup(employeeId, data);
      const newSetup = res?.data?.data?.paySetup;
      if (newSetup) {
        const formatted = { ...newSetup, id: newSetup._id };
        setPaySetups((p) => {
          const exists = p.find((s) => (s.employee && s.employee._id === employeeId) || s.employeeId === employeeId);
          if (exists) return p.map((s) => ((s.employee && s.employee._id === employeeId) || s.employeeId === employeeId ? formatted : s));
          return [...p, formatted];
        });
        return;
      }
    } catch (e) {
      console.warn("API savePaySetup failed", e);
    }
  }, []);

  // ── Payslips ─────────────────────────────────────────────────────────────
  const generatePayslip = useCallback(async (data) => {
    try {
      const res = await hrService.generatePayslip(data);
      const newPsl = res?.data?.data?.payslip;
      if (newPsl) {
        const formatted = { ...newPsl, id: newPsl._id };
        setPayslips((p) => [formatted, ...p]);
        return formatted;
      }
    } catch (e) {
      console.warn("API generatePayslip failed", e);
    }
    return null;
  }, []);

  const bulkGeneratePayslips = useCallback(async (data) => {
    try {
      const res = await hrService.bulkGeneratePayslips(data);
      if (res && res.data?.status === 'success') {
        try {
          const pslRes = await hrService.getPayslips();
          if (pslRes && pslRes.data?.data) {
            setPayslips(pslRes.data.data.map(p => ({ ...p, id: p._id })));
          }
        } catch (fetchErr) {
          console.warn("Failed to refetch payslips after bulk generation", fetchErr);
        }
        return res.data;
      }
    } catch (e) {
      console.warn("API bulkGeneratePayslips failed", e);
      throw e;
    }
    return null;
  }, []);

  const markPayslipPaid = useCallback(async (id) => {
    try {
      const res = await hrService.updatePayslipStatus(id, "paid");
      const updatedPsl = res?.data?.data?.payslip;
      if (updatedPsl) {
        const formatted = { ...updatedPsl, id: updatedPsl._id };
        setPayslips((p) => p.map((ps) => (ps._id === id || ps.id === id ? formatted : ps)));
        return;
      }
    } catch (e) {
      console.warn("API updatePayslipStatus failed", e);
    }
  }, []);

  const deletePayslip = useCallback(async (id) => {
    try {
      await hrService.deletePayslip(id);
      setPayslips((p) => p.filter((ps) => ps.id !== id && ps._id !== id));
    } catch (e) {
      console.warn("API deletePayslip failed", e);
    }
  }, []);

  return (
    <HRContext.Provider
      value={{
        employees, addEmployee, updateEmployee, toggleEmployeeStatus,
        attendance, markAttendance, bulkMarkAttendance,
        leaves, applyLeave, updateLeaveStatus, deleteLeave,
        paySetups, savePaySetup,
        payslips, generatePayslip, bulkGeneratePayslips, markPayslipPaid, deletePayslip,
        shifts, addShift, editShift, removeShift,
        dashboardStats, loadingStats, refreshDashboardStats,
        isLoading
      }}
    >
      {children}
    </HRContext.Provider>
  );
}
